import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { CircularProgress } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Button";
import ShowToast from "../../components/alerts/all_toasts";
import NoWalletNotice from "../../components/NoWalletNotice";
import BaseService from "../../helpers/baseServices";
import { RouteConstants } from "../../helpers/RouteConstants";

const digitsOnly = (value: string) => value.replace(/\D/g, "").slice(0, 4);

const ChangePin = () => {
  const navigate = useNavigate();
  const [walletId, setWalletId] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const getWallet = () => {
    setIsFetching(true);
    BaseService.get_api(`${RouteConstants.WALLET_ROUTE}?limit=1&offset=0`)
      .then((res) => setWalletId(res?.data?.payload?.items?.[0]?.id ?? null))
      .catch(() => setWalletId(null))
      .finally(() => setIsFetching(false));
  };

  useEffect(() => {
    getWallet();
  }, []);

  const handlePinChange = async () => {
    if (newPin.length !== 4) {
      setError("Your new PIN must be exactly 4 digits.");
      return;
    }
    if (newPin !== confirmPin) {
      setError("PINs don't match.");
      return;
    }
    if (!password) {
      setError("Enter your password to confirm.");
      return;
    }
    setError("");
    setIsBusy(true);
    try {
      await BaseService.post_api(
        `${RouteConstants.WALLET_ROUTE}/${walletId}/change-pin`,
        { pin: newPin, password },
      );
      ShowToast.success_toast("Wallet PIN changed");
      navigate("/settings");
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Fragment>
      <div className="h-screen overflow-hidden bg-neutral-50 dark:bg-dark-100">
        <div className="h-full mt-5 mx-[20px]">
          <div className="flex justify-between items-center">
            <div className="flex-[0.3]">
              <button
                className="h-10 text-neutral-300 flex justify-center items-center"
                onClick={() => navigate(-1)}
              >
                <ChevronLeftIcon className="h-6 w-6 font-bold" />
              </button>
            </div>
            <div className="flex-[0.7]">
              <h4 className="text-header-4">Wallet PIN</h4>
            </div>
          </div>

          {isFetching ? (
            <div className="mt-10 flex justify-center">
              <CircularProgress size={28} />
            </div>
          ) : !walletId ? (
            <div className="mt-8">
              <NoWalletNotice onCreated={getWallet} />
            </div>
          ) : (
            <>
              <div className="body-text-2 mt-6">
                Set a new 4-digit PIN for paying and withdrawing from your
                wallet.
              </div>
              <div className="flex flex-col mt-6">
                <small className="mb-2">New PIN</small>
                <input
                  className="input"
                  type="password"
                  inputMode="numeric"
                  value={newPin}
                  onChange={(e) => setNewPin(digitsOnly(e.target.value))}
                  placeholder="4 digits"
                  disabled={isBusy}
                />
              </div>
              <div className="flex flex-col mt-4">
                <small className="mb-2">Confirm new PIN</small>
                <input
                  className="input"
                  type="password"
                  inputMode="numeric"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(digitsOnly(e.target.value))}
                  placeholder="Re-enter your new PIN"
                  disabled={isBusy}
                />
              </div>
              <div className="flex flex-col mt-4">
                <small className="mb-2">Your password</small>
                <input
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="The password you sign in with"
                  disabled={isBusy}
                />
              </div>
              {error && <div className="text-red-500 mt-2">{error}</div>}

              <div className="mt-[40px]">
                <Button width="w-full" disabled={isBusy} onClick={handlePinChange}>
                  {isBusy ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    <span>Change PIN</span>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default ChangePin;
